import type { JSX } from 'react';
import { DecoratorNode, type NodeKey, type SerializedLexicalNode, type DOMExportOutput, type DOMConversionMap, type DOMConversionOutput } from 'lexical';

export interface ImagePayload {
  src: string;
  altText?: string;
  maxWidth?: number;
  width?: number;
  height?: number;
}

export type SerializedImageNode = SerializedLexicalNode & {
  type: 'image';
  version: 1;
  src: string;
  altText: string;
  maxWidth: number;
  width: number;
  height: number;
};

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (domNode instanceof HTMLImageElement) {
    const { src, alt } = domNode;
    const node = $createImageNode({ src, altText: alt || '' });
    return { node };
  }
  return null;
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __maxWidth: number;
  __width: number;
  __height: number;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__key,
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, maxWidth, width, height } = serializedNode;
    const node = $createImageNode({ src, altText, maxWidth, width, height });
    return node;
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      altText: this.__altText,
      maxWidth: this.__maxWidth,
      width: this.__width,
      height: this.__height,
    };
  }

  constructor(
    src: string,
    altText: string,
    maxWidth?: number,
    width?: number,
    height?: number,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText || '';
    this.__maxWidth = maxWidth || 800;
    this.__width = width || 0;
    this.__height = height || 0;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'editor-image-container';
    return span;
  }

  updateDOM(): false {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.style.maxWidth = '100%';
    element.style.height = 'auto';
    element.style.borderRadius = '12px';
    element.style.margin = '12px 0';
    element.style.display = 'block';
    return { element };
  }

  decorate(): JSX.Element {
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        style={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '12px',
          margin: '12px 0',
          display: 'block',
        }}
        draggable={false}
      />
    );
  }
}

export function $createImageNode({ src, altText, maxWidth, width, height }: ImagePayload): ImageNode {
  return new ImageNode(src, altText || '', maxWidth, width, height);
}

export function $isImageNode(node: unknown): node is ImageNode {
  return node instanceof ImageNode;
}